// Hero for Signature WAF drawer — verdict banner + tags (scrolls with body).

import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Stack, Typography } from "@mui/material";

import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import colors from "../../../../../utils/colors";
import {
  drawerTagChipSx,
  getRiskLevelConfig,
  getStatusConfig,
  BLOCKED_STATUS_FG,
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "../wafEventDrawerShared";
import { formatWafAttackTypeLabel } from "../wafEventListUtils";
import {
  spBlockedBannerSx,
  spBlockIconBoxSx,
  spHeroContentSx,
  spLh,
} from "./smartParseDrawerUi";

export default function SignatureWafDrawerHero({ view }) {
  if (!view) return null;

  const statusConfig = getStatusConfig(view.statusString);
  const riskConfig = getRiskLevelConfig(view.riskLevel);
  const isBlocked = view.statusString === "Blocked";
  const isChallenged = view.statusString === "Challenged";

  const iconColor = isBlocked
    ? colors.red[100]
    : isChallenged
      ? colors.yellow[200]
      : colors.green[300];

  return (
    <Box sx={spHeroContentSx}>
      <Box sx={spBlockedBannerSx}>
        <Stack direction="row" sx={{ gap: "15px", alignItems: "center", minWidth: 0, position: "relative", zIndex: 1 }}>
          <Box sx={spBlockIconBoxSx}>
            <LayerdIcon
              icon={isBlocked ? DETECTION_ICONS.requestBlocked : DETECTION_ICONS.request}
              size={24}
              color={iconColor}
            />
          </Box>
          <Stack sx={{ minWidth: 0, flex: 1, gap: "8px" }}>
            <Typography
              sx={{
                fontSize: 21,
                fontWeight: 700,
                lineHeight: spLh.tight,
                letterSpacing: "-0.01em",
                color: isBlocked ? BLOCKED_STATUS_FG : TEXT_PRIMARY,
              }}
            >
              {view.statusString || "Detection"}
            </Typography>
            {view.responseDetails?.message && (
              <Typography sx={{ fontSize: 12.5, lineHeight: spLh.relaxed, color: TEXT_SECONDARY }}>
                {view.responseDetails.message}
                {view.responseDetails.statusCode != null && (
                  <>
                    {" · HTTP "}
                    <Typography component="span" sx={{ color: statusConfig.textColor, fontWeight: 600 }}>
                      {view.responseDetails.statusCode}
                    </Typography>
                  </>
                )}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: "8px" }}>
        <Chip
          label={view.detectionSummary.verdict}
          size="small"
          sx={{
            ...drawerTagChipSx,
            backgroundColor: view.verdict === "malicious" ? "#FEE2E2" : "#D1FAE5",
            color: view.verdict === "malicious" ? "#B91C1C" : "#065F46",
          }}
        />
        {view.riskLevel && view.riskLevel !== "unknown" && riskConfig && (
          <Chip
            label={riskConfig.label}
            size="small"
            sx={{ ...drawerTagChipSx, backgroundColor: riskConfig.bgColor, color: riskConfig.color }}
          />
        )}
        {view.attackTypes.map((type) => (
          <Chip
            key={type}
            label={formatWafAttackTypeLabel(type) || type}
            size="small"
            sx={{ ...drawerTagChipSx, backgroundColor: "#EEF0FE", color: PRIMARY_COLOR }}
          />
        ))}
      </Stack>

      {view.summary && (
        <Box
          sx={{
            py: "10px",
            px: "12px",
            backgroundColor: "#F8F9FF",
            borderRadius: "8px",
            border: "1px solid #E5E7FF",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#4B5563", lineHeight: spLh.relaxed }}>
            {view.summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

SignatureWafDrawerHero.propTypes = {
  view: PropTypes.object,
};
